import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchTriggerDetail } from '../backend/fetchs';
import { Trigger } from '../backend/interfaces';
import DateCell from '../components/DateCell';
import DirectionCell from '../components/DirectionCell';
import IntervalCell from '../components/IntervalCell';
import FilterStatusCell from '../components/FilterStatusCell';

const TriggerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Trigger | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const result = await fetchTriggerDetail(parseInt(id));
        setData(result);
        setError(null);
      } catch (err) {
        setError('Error fetching trigger detail');
        console.error('Error fetching trigger detail:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center">
        <div className="text-red-500 text-xl mb-4">{error || 'Trigger not found'}</div>
        <Link
          to="/triggers"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Triggers
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 8,
    }).format(price);
  };

  const formatVolume = (volume: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(volume);
  };

  const getExplanationItems = () => {
    const isLong = data.side.description === 'LONG';
    
    const items = [
      {
        text: `The StochRSI lines are crossing ${isLong ? 'below' : 'above'} the ${isLong ? '20' : '80'} level.`,
        check: true,
      }
    ];

    // Add volume filter check
    if (data.filters.volume.status) {
      items.push({
        text: 'The optional Volume filter condition has also been met.',
        check: true,
      });
    }

    // Add RSI filter check
    if (data.filters.rsi.status) {
      items.push({
        text: 'The optional RSI filter condition has also been met.',
        check: true,
      });
    }

    // Add Engulfing filter check
    if (data.filters.engulfing.status) {
      items.push({
        text: 'The optional Engulfing filter condition has also been met.',
        check: true,
      });
    }

    // Add Candlestick Patterns filter check
    if (data.filters.candlestick_type.status) {
      items.push({
        text: 'The optional Candlestick Patterns filter condition has also been met.',
        check: true,
      });
    }

    // Add trend alignment check
    items.push({
      text: `Aligned with trend confirmation which is ${data.trend_confirmation.side.description}`,
      check: data.side.description === data.trend_confirmation.side.description,
    });

    // add daily bias alignment check
    items.push({
      text: `Aligned with daily bias which is ${data.trend_confirmation.daily_bias.side.description}`,
      check: data.side.description === data.trend_confirmation.daily_bias.side.description,
    });

    return items;
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Trigger #{data.id}</h1>
        </div>
        
        {/* Main Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-sm font-medium text-gray-500">Ticker</h2>
              <p className="text-2xl font-bold text-gray-900">{data.ticker.description}</p>
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Trigger Confirmation</h2>
              <DirectionCell direction={data.side.description} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Confirmation Date</h2>
              <DateCell timestamp={data.created_at} blackMode={true} />
            </div>
            <div>
              <h2 className="text-sm font-medium text-gray-500">Interval</h2>
              <IntervalCell interval={data.candlestick.interval.description} />
            </div>
          </div>
        </div>
      </div>

      {/* Indicators Table */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Indicators</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">Price</h3>
            <p className="text-lg font-semibold text-gray-900">{formatPrice(data.indicators.price)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">StochRSI (K)</h3>
            <p className="text-lg font-semibold text-gray-900">{data.indicators.stochrsi_k.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">StochRSI (D)</h3>
            <p className="text-lg font-semibold text-gray-900">{data.indicators.stochrsi_d.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">StochRSI</h3>
            <p className="text-lg font-semibold text-gray-900">
              <DirectionCell direction={data.indicators.stochrsi === 1 ? 'LONG' : 'SHORT'} />
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">RSI (14 periods)</h3>
            <p className="text-lg font-semibold text-gray-900">{data.indicators.rsi_14.toFixed(2)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">Volume (10 periods)</h3>
            <p className="text-lg font-semibold text-gray-900">{formatVolume(data.indicators.volume_10)}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-500">Trend Direction</h3>
            <DirectionCell direction={data.trend_confirmation.side.description} />
          </div>
          {data.trend_confirmation.daily_bias && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500">Daily Bias</h3>
              <DirectionCell direction={data.trend_confirmation.daily_bias.side.description} />
            </div>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Volume Filter</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <FilterStatusCell status={data.filters.volume.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current Value:</span>
                <span className="text-sm font-medium text-gray-900">{formatVolume(data.filters.volume.value)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Threshold:</span>
                <span className="text-sm font-medium text-gray-900">{formatVolume(data.filters.volume.threshold)}</span>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">RSI Filter</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <span className={`text-sm font-medium ${data.filters.rsi.status ? 'text-green-600' : 'text-red-600'}`}>
                  <FilterStatusCell status={data.filters.rsi.status} />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Current Value:</span>
                <span className="text-sm font-medium text-gray-900">{data.filters.rsi.value.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Threshold:</span>
                <span className="text-sm font-medium text-gray-900">{data.filters.rsi.threshold.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Engulfing Filter</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <FilterStatusCell status={data.filters.engulfing.status} />
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Candlestick Patterns Filter</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <FilterStatusCell status={data.filters.candlestick_type.status} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Explanation</h2>
        <div className="space-y-3">
          {getExplanationItems().map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                item.check ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {item.check ? (
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <span className={`text-md ${item.check ? 'text-green-700' : 'text-red-700'}`}>
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Candlestick Patterns */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Candlestick Patterns</h2>
        {data.candlestick_types.length > 0 ? (
          <div className="space-y-4">
            {data.candlestick_types.map((pattern, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{pattern.title}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    pattern.action === 'Compra' ? 'bg-green-100 text-green-800' :
                    pattern.action === 'Venta' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {pattern.action}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{pattern.description}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-500 text-lg mb-2">No candlestick patterns found</div>
            <p className="text-gray-400 text-sm">No specific candlestick patterns were detected for this trigger.</p>
          </div>
        )}
      </div>

      {/* Candlestick Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Candlestick Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open Date</h3>
              <DateCell timestamp={data.candlestick.open_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close Date</h3>
              <DateCell timestamp={data.candlestick.close_time / 1000} blackMode={true} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Volume</h3>
              <p className="text-lg font-semibold text-gray-900">{formatVolume(data.candlestick.volume)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Number of Trades</h3>
              <p className="text-lg font-semibold text-gray-900">
                {data.candlestick.number_of_trades.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Open</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.open)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Close</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.close)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">High</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.high)}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Low</h3>
              <p className="text-lg font-semibold text-gray-900">{formatPrice(data.candlestick.low)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TriggerDetailPage; 